import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { HttpError, type CardDetail, type CardId, type TripView } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import { conversationRepository, tripRepository } from '@web/data/repositories';

import { useCardDetail } from './use_card_detail';
import { useOrgContext } from './use_org_context';
import { fireAndForget } from '@web/ui/core/utils';

export type CardTripState =
  | { status: 'none' }
  /**
   * `tripKnown` separa "ainda não sei se existe viagem" (card/meta pendentes)
   * de "existe viagem e ela está chegando". Sem essa distinção o pai abriria a
   * coluna em todo card assumido e a fecharia ao descobrir que não há viagem
   * — a coluna piscava.
   */
  | { status: 'loading'; tripKnown: boolean }
  | { status: 'ready'; trip: TripView }
  | { status: 'error'; retry: () => void };

function isNotFound(err: unknown): boolean {
  return err instanceof HttpError && err.status === 404;
}

/**
 * Maps one chained query's error into a terminal `CardTripState`, or
 * `undefined` when the caller should keep evaluating (no error to report).
 * A 404 is treated as `'none'` only when `notFoundIsNone` is set — today
 * that is just the trip read (the kanban guard says "not yours"); any
 * other failure at any link becomes `'error'` with a manual `retry`.
 */
function errorState(
  isError: boolean,
  error: unknown,
  retry: () => void,
  notFoundIsNone = false,
): CardTripState | undefined {
  if (!isError) return undefined;
  if (notFoundIsNone && isNotFound(error)) return { status: 'none' };
  return { status: 'error', retry };
}

/**
 * One chained read's full gate: terminal `'error'`/`'none'` on failure,
 * `'loading'` while the data has not arrived yet, or `undefined` once
 * `data` is present and the caller should move on to the next read.
 */
function gate(
  isError: boolean,
  error: unknown,
  data: unknown,
  retry: () => void,
  notFoundIsNone = false,
): CardTripState | undefined {
  return (
    errorState(isError, error, retry, notFoundIsNone) ??
    (data === undefined ? { status: 'loading', tripKnown: false } : undefined)
  );
}

/**
 * Marca o `'loading'` que já sai do gate da VIAGEM: aqui o `tripId` está
 * resolvido, então o pai pode abrir a coluna sabendo que ela tem conteúdo a
 * caminho. Os demais estados passam intactos.
 */
function withTripKnown(state: CardTripState): CardTripState {
  return state.status === 'loading' ? { status: 'loading', tripKnown: true } : state;
}

/**
 * The trip linked to a card's conversation, for the operator's trip column.
 * Three chained reads, each gated on the previous one:
 *   card detail (kanban) → `conversationId` + `customer.id`
 *   conversation meta (conversation-api) → `tripId`
 *   trip app view (kanban proxy → trip-api) → `TripView`
 *
 * `'none'` covers every "there is simply no trip to show" outcome — no
 * conversation ref, no `tripId`, or a 404 from the kanban guard (the trip is
 * not this traveler's). Only unexpected failures surface as `'error'`, so the
 * column never tells the operator "erro" for a card that just has no trip.
 */
export function useCardTrip(cardId: CardId | undefined): CardTripState {
  const state = useCardTripOf(useCardDetail(cardId));
  return cardId ? state : { status: 'none' };
}

/**
 * The same chain from a card read the caller already holds: the traveler view
 * reads its card by conversation, not by id (`useCardDetail`), and names that
 * conversation — a card may carry more than one. Without it, the card's first
 * conversation ref.
 */
export function useCardTripOf(
  detailQuery: UseQueryResult<CardDetail>,
  requestedConversationId?: string,
): CardTripState {
  const { orgId } = useOrgContext();
  const detail = detailQuery.data;
  const conversationId =
    requestedConversationId ??
    detail?.externalRefs.find((r) => r.system === 'conversation')?.externalId;
  const customerId = detail?.customer.id;

  const metaQuery = useQuery({
    queryKey: queryKeys.conversation.meta(conversationId ?? ''),
    queryFn: () => conversationRepository.loadMeta(conversationId as string),
    enabled: !!conversationId,
  });
  const tripId = metaQuery.data?.tripId;

  const tripQuery = useQuery({
    queryKey: queryKeys.trips.detail(orgId, customerId ?? '', tripId ?? ''),
    queryFn: () => tripRepository.getCustomerTrip(orgId, customerId as string, tripId as string),
    enabled: !!customerId && !!tripId,
    // A 404 is a final answer (guard says "not yours"); retrying would only
    // hammer trip-api through the proxy. Anything else gets one automatic
    // retry, matching the app's production default, so a transient trip-api
    // 503 self-heals before the operator ever needs the manual `retry()`.
    retry: (count, err) => !isNotFound(err) && count < 1,
    // The retry COUNT matches the app's production `retry: 1` default; the
    // DELAY is deliberately 0 (not the default backoff) — one immediate
    // extra attempt is enough for this single bounded retry, and the
    // operator column shouldn't sit through exponential backoff waiting on it.
    retryDelay: 0,
  });

  const detailGate = gate(detailQuery.isError, detailQuery.error, detail, () => {
    fireAndForget(detailQuery.refetch());
  });
  if (detailGate) return detailGate;
  if (!conversationId) return { status: 'none' };

  const metaGate = gate(metaQuery.isError, metaQuery.error, metaQuery.data, () => {
    fireAndForget(metaQuery.refetch());
  });
  if (metaGate) return metaGate;
  if (!tripId) return { status: 'none' };

  const tripGate = gate(
    tripQuery.isError,
    tripQuery.error,
    tripQuery.data,
    () => {
      fireAndForget(tripQuery.refetch());
    },
    true,
  );
  if (tripGate) return withTripKnown(tripGate);
  // `tripGate` above already returned 'loading' when `tripQuery.data` is
  // undefined, so the assertion is safe — `gate`'s `data: unknown` parameter
  // just loses that link for TS.
  return { status: 'ready', trip: tripQuery.data as TripView };
}
